import * as rpc from '@platform/components/rpc';
import { setRpcAuthData } from '@platform/components/rpc';
import config from '@platform/shared/config';
import { NodeAPI } from '@platform/shared/rpc/node';
import type { RpcSysParams } from '@platform/store/user';
import UserStore, { sysRoles } from '@platform/store/user';
import { normalize } from '@platform/shared/helpers/formatters';
import { statusAndColumnSetting } from '@platform/store/status-and-column-setting';
import type { sys_option } from '@platform/layouts/sys_option/sys_option';
import type { SysOptionEntity } from '@platform/entities/sys_option/sys_option.entity';
import { stableJsonStringify } from '@platform/shared/helpers/role-helpers';
import { matches } from 'lodash-es';
import { NAME } from '@platform/components/docflow/statuses';

type IRoutineResult = void | Promise<void>;
const routines = {
    load: [] as (() => IRoutineResult)[],
    tokenUpdated: [] as ((token: string | null | undefined, sysParams?: Partial<RpcSysParams>) => IRoutineResult)[],
    authUserState: [] as ((token: string, userState: typeof UserStore.userState) => IRoutineResult)[],
    auth: [] as ((token: string, userState: typeof UserStore.userState, nodeToken: string) => IRoutineResult)[],
    socketAuth: [] as ((authData: { token: string; sysParams: RpcSysParams } | null) => IRoutineResult)[],
    logout: [] as (() => IRoutineResult)[],
};

const logoutTimer = {
    ignore: false,
};
const socketRefreshState = {
    inFlight: null as Promise<boolean> | null,
};

export class CancelRequestError extends Error {}

const runHooks = <T extends keyof typeof routines>(type: T, ...args: Parameters<(typeof routines)[T][number]>) => {
    for (const hook of routines[type]) {
        try {
            // @ts-ignore
            const result = hook.call(null, ...args);
            if (result instanceof Promise) result.catch(console.warn);
        } catch (err) {
            console.warn(err);
        }
    }
};
const saveToken = (token: string | null, nodeToken: string | null) => {
    if (token && token !== 'null' && token !== 'undefined') localStorage.setItem(config.idToken, token);
    else localStorage.removeItem(config.idToken);
    if (nodeToken && nodeToken !== 'null' && nodeToken !== 'undefined')
        localStorage.setItem(config.idToken + '_node', nodeToken);
    else localStorage.removeItem(config.idToken + '_node');
};
const getRefreshToken = (): string | null => {
    const fromLS = localStorage.getItem('REFRESH_TOKEN');
    if (!fromLS) return null;
    const parsed = JSON.parse(fromLS);
    const expiresAt = new Date(Date.parse(parsed.expires));
    if (new Date().getTime() >= expiresAt.getTime()) return null;
    return parsed.refreshToken as string;
};
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default {
    runHooks,
    addRoutine<T extends keyof typeof routines>(on: T, hook: (typeof routines)[T][number]) {
        if (!(on in routines)) throw new Error(`No such routine type: ${on}. Available is ${Object.keys(routines)}`);

        // @ts-ignore
        routines[on].push(hook);
        return () => {
            // @ts-ignore
            routines[on] = [...(routines[on] || [])].filter((r) => r !== hook);
        };
    },

    onLoad(token?: string, nodeToken?: string) {
        runHooks('load');
        let authToken: string | null = token || localStorage.getItem(config.idToken);
        let authTokenNode: string | null = nodeToken || localStorage.getItem(config.idToken + '_node');
        if (authToken === 'null' || authToken === 'undefined') authToken = null;
        if (authTokenNode === 'null' || authTokenNode === 'undefined') authTokenNode = null;
        saveToken(authToken, authTokenNode);
        if (!authToken || !authTokenNode) {
            UserStore.setAsNotLoggedIn();
            return null;
        }
        runHooks('tokenUpdated', authTokenNode, {});
        setRpcAuthData(authToken);
        return true;
    },
    onLogout(redirectAfterLogin = false) {
        if (logoutTimer.ignore) return;
        logoutTimer.ignore = true;
        setTimeout(() => (logoutTimer.ignore = false), 400);
        if (redirectAfterLogin) {
            const redirectTo = `${window.location.pathname}${window.location.search}`;
            localStorage.setItem('login_redirect', JSON.stringify({ when: new Date().toISOString(), redirectTo }));
        }
        saveToken(null, null);
        setRpcAuthData(null);
        runHooks('tokenUpdated', null, {});
        runHooks('socketAuth', null);
        runHooks('logout');
        UserStore.setAsNotLoggedIn();
    },
    async fetchUserData(userRes?: any | null, mobileView = false) {
        if (!userRes) {
            userRes = await NodeAPI.AuthService.getCurrentUser().catch((err) => {
                if (err instanceof CancelRequestError) {
                    return { _canceled: true };
                }
                console.warn(err);
                return null;
            });
        }
        if (!userRes) {
            this.onLogout(true);
            return null;
        }
        let role = userRes.role;
        if (role?.capabilities && Array.isArray(role.capabilities)) {
            role.capabilities = new Map(
                role.capabilities
                    .map((v: any) => {
                        if (v.type === 'component') {
                            if (v.component_props) {
                                v.arePropsMatches = matches(v.component_props);
                            }
                            return [`component:${v.component}^${stableJsonStringify(v.component_props)}`, v];
                        } else if (v.type === 'fn') {
                            return [`fn:${v.fn_name}`, v];
                        } else if (v.type === 'group') {
                            return null;
                        }
                    })
                    .filter(Boolean)
            );
        } else {
            role = {
                capabilities: new Map(),
                roleItems: [],
            };
            if (userRes.is_superadmin) role.is_superadmin = true;
        }
        UserStore.setSysParams({
            ...UserStore.userState.sysParams,
            currUserUuid: userRes?.id,
            currExtUserId: undefined,
            sysOrgId: null,
        });
        const orgsRes: any[] | null = await NodeAPI.AuthService.getServiceOrgHierarchyList().catch((err) => {
            console.warn(err);
            return null;
        });
        if (!orgsRes) {
            this.onLogout();
            return null;
        }
        const orgState = {};
        const orgs: any[] = orgsRes.map((org: any) => {
            org.roles = org.role_code?.split(', ') || [];
            return org;
        });
        const mainOrg = orgs.find((org) => org.main);
        Object.assign(mainOrg, {
            label: mainOrg.name,
            value: mainOrg.id,
        });
        Object.assign(orgState, {
            all: orgs,
            main: mainOrg,
        });
        UserStore.setSysParams({
            ...UserStore.userState.sysParams,
            sysOrgId: mainOrg.id,
            currUserUuid: userRes.id,
            currExtUserId: userRes?.ext_user_id,
        });
        runHooks('authUserState', '', UserStore.userState);
        const currentEmployeeProjectsId = (await rpc.funcGetCurrentEmployeeProjectsId(
            userRes?.currentEmployeeId
        )) as string[];
        UserStore.setUserState(
            {
                ...userRes,
                username: userRes?.login,
                isGuest: mainOrg.roles.includes(sysRoles.GUEST),
                fio: {
                    name: userRes?.fio_name,
                    surname: userRes?.fio_surname,
                    secname: userRes?.fio_secname,
                },
                role,
                extUserId: +userRes?.ext_user_id,
                currentEmployeeProjectsId: currentEmployeeProjectsId,
            },
            orgState
        );

        const token: string = localStorage.getItem(config.idToken) || '';
        const tokenNode: string = localStorage.getItem(config.idToken + '_node') || '';
        runHooks('authUserState', token, UserStore.userState);

        if (role?.roleItems && !mobileView) await this.fetchUserSettingData(role);
        await this.userSysOption();
        runHooks('auth', token, UserStore.userState, tokenNode);
        runHooks('socketAuth', {
            token: tokenNode,
            sysParams: UserStore.userState.sysParams,
        });

        return { userRes, orgsRes };
    },

    refreshSocketAuth() {
        // Не запускаем параллельные рефреши, чтобы не переинициализировать сокеты со старым токеном.
        if (socketRefreshState.inFlight) return socketRefreshState.inFlight;
        socketRefreshState.inFlight = (async () => {
            const refreshToken = getRefreshToken();
            // Без валидного refresh token переавторизация сокета невозможна.
            if (!refreshToken) {
                console.warn('Socket auth refresh skipped: refresh token missing or expired.');
                return false;
            }
            for (let attempt = 1; attempt <= 3; attempt++) {
                try {
                    // Обновляем токены по HTTP и затем применяем новый access token к socket auth.
                    const resp = await NodeAPI.AuthService.refreshTokens({
                        body: { refresh_token: refreshToken },
                    });
                    localStorage.setItem(
                        'REFRESH_TOKEN',
                        JSON.stringify({
                            refreshToken: resp.refresh_token,
                            expires: resp.refresh_token_expires_at,
                        })
                    );
                    const currentToken = localStorage.getItem(config.idToken);
                    const loadResult = this.onLoad(currentToken || '<refreshed>', resp.access_token);
                    if (!loadResult) {
                        console.warn('Socket auth refresh failed: cannot apply new tokens.');
                        return false;
                    }
                    runHooks('socketAuth', {
                        token: resp.access_token,
                        sysParams: UserStore.userState.sysParams,
                    });
                    return true;
                } catch (err) {
                    const status = (err as any)?.response?.status;
                    const code = (err as any)?.code;
                    const reason = status ? `status=${status}` : code ? `code=${code}` : 'unknown error';
                    // Небольшой retry на случай кратковременных сетевых/серверных проблем.
                    if (attempt < 3) await delay(5000);
                    else console.warn(`Socket auth refresh failed after 3 attempts (${reason}).`, err);
                }
            }
            return false;
        })().finally(() => {
            socketRefreshState.inFlight = null;
        });
        return socketRefreshState.inFlight;
    },

    async fetchUserSettingData(role: { roleItems: { value: string; name: string }[] }) {
        // region Настройка колонок и смена статусов
        // Получаем список настроек
        const sysDocStateSettingList = await rpc
            .funcGetCurrentSysDocStateSettingByUserRolesIdListIn(
                role.roleItems?.map((r: any) => `'${r.value}'`),
                config.serviceId,
                UserStore?.userState.sysParams.sysOrgId
            )
            .catch((err) => {
                console.warn(err);
                return Promise.resolve(null);
            });

        // Для настройка колонок и статусов будет использоваться два раздела
        // statuses - переходы по статусам (в завимости от статуса может быть тоже настройки колонок)
        // columns - колонки - редактирование, скрытие, расшаривание
        // sys_crud_function_setting - настройка crud_function (чтение / сохранение / удаление / добавление)
        const sysDocStateSetting = await statusAndColumnSetting(sysDocStateSettingList);

        UserStore.setSysDocStateSetting(sysDocStateSetting);
        //endregion
    },

    async userSysOption() {
        const options = (await rpc.funcGetSysOption(
            config.serviceId,
            UserStore.userState.sysParams.sysOrgId,
            UserStore.userState.sysParams.currUserUuid
        )) as SysOptionEntity[];

        const userOptionList = {
            pusk$budget: {
                DISPLAY_PRICES_IN_THE_INVOICE_PROJECT: false,
                // PUSK-6128: по умолчанию дата акта заполняется автоматически
                AUTO_FILL_ACT_DATE: true,
            },
            platform$account: {
                IS_OUR_COMPANY_NAME_TEMPLATE: null,
                HAVING_A_CONTROLLED_ACCOUNT_TEMPLATE: null,
            },
            pusk$payment_register: {
                CONSIDER_TEMPLATE: [],
                // Default как на бэкенде (`register.invoice_payment_fill`): опции нет → реестр учитывается.
                with_payment_register: true,
            },
            platform$invoice: {
                CONSIDER_TEMPLATE: [],
                IS_SHOW_PAYMENT_TYPE: false,
                AUTO_CREATE_DATE_DELIVERY_FROM_REQUEST: false,
                INVOICE_BUDGET_STATE_REQUIRED: false,
                AI_ENABLE_INVOICE_POSITION_COMPARISON_AUTO: false,
                // PUSK-6034: создание акта на основании счета скрыто у всех, кроме явно включивших организаций
                ENABLE_CREATE_ACT_FROM_INVOICE: false,
                // PUSK-6193: ФИО согласующего в колонке состояния реестра счетов
                SHOW_APPROVER_IN_STATUS_COLUMN: false,
            },
            platform$act: {
                CONSIDER_TEMPLATE: [],
                // PUSK-6180: бэкенд трактует отсутствие настройки как «сопоставлять»
                ENABLE_ACT_POSITION_COMPARISON_AUTO: true,
            },
            pusk$commercial_offer: {
                DOC_STATE_FOR_NEW_INVOICE: null,
                AI_ENABLE_COMMERCIAL_OFFER_POSITION_COMPARISON: null,
                AI_ENABLE_COMMERCIAL_OFFER_POSITION_COMPARISON_AUTO: null,
                MATCHING_V2_ENABLED: null,
                USE_EMBEDDING_MATCHING: null,
                COMMERCIAL_OFFER_AUTO_CREATE: null,
                AI_POSITION_COMPARISON_MODEL: null,
                AI_POSITION_COMPARISON_REASONING_EFFORT: 'none',
                AI_POSITION_COMPARISON_TEMPERATURE: 1,
                PLANNED_PAYMENT_DATE_REQUIRED: null,
            },
            pusk$request: {
                DEFAULT_APPROVING_EMPLOYEE_FOR_LAST_STAGE: null,
                MINIMUM_LIMIT_DELIVERY_DAYS: 1,
                SHOW_APPLICANT_FIELD: false,
            },
            fileStore$file_store: {
                AUTOMATIC_FILE_RENAMING: [],
                ENABLE_FILE_RECOVERY: false,
            },

            ai$settings: {
                ai_enable_chat: false,
                ai_chatgpt_enable_chat: false,
                ai_yandexgpt_enable_chat: false,
                ai_deepseek_enable_chat: false,
                ai_default_model: null,
                ai_chatgpt_version: [],
                ai_yandexgpt_version: [],
                ai_deepseek_version: [],
                ai_chatgpt_temperature: 1.0,
                ai_yandexgpt_temperature: 1.0,
                ai_deepseek_temperature: 1.0,
                ai_temperature: 1.0,
                ai_reasoning_effort: 'none',
                ai_deepseek_reasoning_effort: 'none',
                ai_yandexgpt_folder_id: null,
            },

            pusk$invoiceList: {
                INVOICE_LIST_SORT_BY_INVOICE_DATE: false,
            },
            platform$contragent: {
                COUNTRY: 'RU',
                DEFAULT_NDS: 1,
            },

            platform$email: {
                /** Показывать реестр почты */
                ENABLE_EMAIL_REGISTRY: false,
            },

            platform$recognized_document: {
                /** сравнение документа со скан файлом */
                available_service: {
                    'platform.invoice': 'ENTERA',
                    'pusk.commercial_offer': 'ENTERA',
                    'platform.act': 'ENTERA',
                },
                ENABLE_DOC_COMPLIANCE_FOR_ACT: false,
                ENABLE_DOC_COMPLIANCE_FOR_INVOICE: false,
                IGNORE_INCORRECT_INN: false,
                GPT_MODEL: null,
                GPT_REASONING_EFFORT: 'none',
                GPT_TEMPERATURE: 1,
            },

            platform$contract: {
                ENABLE_WORK_VOLUME: false,
            },
            pusk$project_budget: {
                USE_PROJECT_BUDGET: true,
            },
            pusk$warehouse: {
                KEEP_WAREHOUSE_ACCOUNTING: false,
                AUTO_CREATE_WAREHOUSE_ON_NEW_PROJECT: false,
            },
            //"Мультифирменный" учет
            // MULTIPLE_ORGANIZATION: {
            //     USE: false,
            //     SHOW_COLUMN_ORG: false,
            //     SHOW_FILTER_ORG: false,
            // },
        } as sys_option;

        if (options?.length) {
            options.forEach((opt: SysOptionEntity) => {
                if (opt?.option) {
                    if (!normalize(opt.sys_user_id) && opt.sys_metadata_id) {
                        if (opt.table_schema === 'file_store') {
                            userOptionList.fileStore$file_store = opt.option;
                        } else if (opt.table_schema === 'pusk') {
                            const option = opt.option;
                            if (opt.table_name === 'payment_register') {
                                userOptionList.pusk$payment_register.CONSIDER_TEMPLATE = option.CONSIDER_TEMPLATE;
                                // PUSK-6161: по этой настройке бэкенд решает, откуда берётся «К оплате»
                                // (реестр на оплату vs сумма счёта) — клиенту она нужна, чтобы считать
                                // локальные суммы теми же формулами. Опции нет → true, как на бэкенде.
                                userOptionList.pusk$payment_register.with_payment_register =
                                    option.with_payment_register ?? true;
                            } else if (opt.table_name === 'commercial_offer') {
                                userOptionList.pusk$commercial_offer.DOC_STATE_FOR_NEW_INVOICE =
                                    option.DOC_STATE_FOR_NEW_INVOICE;
                                userOptionList.pusk$commercial_offer.AI_ENABLE_COMMERCIAL_OFFER_POSITION_COMPARISON =
                                    option.AI_ENABLE_COMMERCIAL_OFFER_POSITION_COMPARISON;
                                userOptionList.pusk$commercial_offer.AI_ENABLE_COMMERCIAL_OFFER_POSITION_COMPARISON_AUTO =
                                    option.AI_ENABLE_COMMERCIAL_OFFER_POSITION_COMPARISON_AUTO;
                                userOptionList.pusk$commercial_offer.COMMERCIAL_OFFER_AUTO_CREATE =
                                    option.COMMERCIAL_OFFER_AUTO_CREATE;
                                userOptionList.pusk$commercial_offer.AI_POSITION_COMPARISON_MODEL =
                                    option.AI_POSITION_COMPARISON_MODEL;
                                userOptionList.pusk$commercial_offer.AI_POSITION_COMPARISON_REASONING_EFFORT =
                                    option.AI_POSITION_COMPARISON_REASONING_EFFORT ?? 'none';
                                userOptionList.pusk$commercial_offer.AI_POSITION_COMPARISON_TEMPERATURE =
                                    option.AI_POSITION_COMPARISON_TEMPERATURE ?? 1;
                                userOptionList.pusk$commercial_offer.PLANNED_PAYMENT_DATE_REQUIRED =
                                    option.PLANNED_PAYMENT_DATE_REQUIRED;
                                userOptionList.pusk$commercial_offer.MATCHING_V2_ENABLED = option.MATCHING_V2_ENABLED;
                                userOptionList.pusk$commercial_offer.USE_EMBEDDING_MATCHING =
                                    option.USE_EMBEDDING_MATCHING;
                            } else if (opt.table_name === 'request') {
                                userOptionList.pusk$request.DEFAULT_APPROVING_EMPLOYEE_FOR_LAST_STAGE =
                                    option.DEFAULT_APPROVING_EMPLOYEE_FOR_LAST_STAGE;

                                userOptionList.pusk$request.DEFAULT_RESPONSIBLE_COORDINATOR_IS_EDITABLE =
                                    option.DEFAULT_RESPONSIBLE_COORDINATOR_IS_EDITABLE;
                                userOptionList.pusk$request.INCLUDE_AUTHOR_IN_APPROVERS =
                                    option.INCLUDE_AUTHOR_IN_APPROVERS ?? true;
                                userOptionList.pusk$request.MINIMUM_LIMIT_DELIVERY_DAYS =
                                    option.MINIMUM_LIMIT_DELIVERY_DAYS ?? 1;
                                userOptionList.pusk$request.APPROVAL_PROCESS_ENABLED = option.APPROVAL_PROCESS_ENABLED;

                                //ЭТО КОСТЫТЬ ДЛЯ ОТКРЫТИЯ РЕДАКТИРОВАНИЯ НОМЕРА ЗВАЯВКИ (В ИНТЕРФЕЙСЕ НЕТ НАСТРОЙКИ) !!!
                                userOptionList.pusk$request.CAN_CHANGE_NUMBER_FIELD = option.CAN_CHANGE_NUMBER_FIELD;

                                userOptionList.pusk$request.AI_ENABLE_REQUEST_CREATE_FROM_AUDIO =
                                    option.AI_ENABLE_REQUEST_CREATE_FROM_AUDIO;
                                userOptionList.pusk$request.REQUEST_BUDGET_STATE_REQUIRED =
                                    option.REQUEST_BUDGET_STATE_REQUIRED ?? false;
                                userOptionList.pusk$request.SHOW_APPLICANT_FIELD =
                                    option.SHOW_APPLICANT_FIELD ?? false;
                            } else if (opt.table_name === 'budget') {
                                userOptionList.pusk$budget.DISPLAY_PRICES_IN_THE_INVOICE_PROJECT =
                                    option.DISPLAY_PRICES_IN_THE_INVOICE_PROJECT;
                                userOptionList.pusk$budget.DOC_STATE_FOR_NEW_ACT = option.DOC_STATE_FOR_NEW_ACT;
                                // по умолчанию true, чтобы для старых записей без ключа поведение не менялось
                                userOptionList.pusk$budget.AUTO_FILL_ACT_DATE = option.AUTO_FILL_ACT_DATE ?? true;
                            } else if (opt.table_name === 'get_invoice_list') {
                                userOptionList.pusk$invoiceList.INVOICE_LIST_SORT_BY_INVOICE_DATE =
                                    option.INVOICE_LIST_SORT_BY_INVOICE_DATE;
                            } else if (opt.table_name === 'warehouse') {
                                userOptionList.pusk$warehouse.KEEP_WAREHOUSE_ACCOUNTING =
                                    option.KEEP_WAREHOUSE_ACCOUNTING;
                                userOptionList.pusk$warehouse.AUTO_CREATE_WAREHOUSE_ON_NEW_PROJECT =
                                    option.AUTO_CREATE_WAREHOUSE_ON_NEW_PROJECT ?? false;
                            } else if (opt.table_name === 'project_budget') {
                                userOptionList.pusk$project_budget.USE_PROJECT_BUDGET =
                                    option.USE_PROJECT_BUDGET ?? true;
                            }
                        } else if (opt.table_schema === 'platform') {
                            const option = opt.option;

                            if (opt.table_name === 'gpt_chat' && option.AI_SETTINGS) {
                                userOptionList.ai$settings = option.AI_SETTINGS;
                            }
                            if (opt.table_name === 'invoice') {
                                userOptionList.platform$invoice.CONSIDER_TEMPLATE = option.CONSIDER_TEMPLATE;
                                userOptionList.platform$invoice.IS_SHOW_PAYMENT_TYPE = option.IS_SHOW_PAYMENT_TYPE;
                                userOptionList.platform$invoice.AUTO_CREATE_DATE_DELIVERY_FROM_REQUEST =
                                    option.AUTO_CREATE_DATE_DELIVERY_FROM_REQUEST;
                                userOptionList.platform$invoice.INVOICE_BUDGET_STATE_REQUIRED =
                                    option.INVOICE_BUDGET_STATE_REQUIRED ?? false;
                                userOptionList.platform$invoice.AI_ENABLE_INVOICE_POSITION_COMPARISON_AUTO =
                                    option.AI_ENABLE_INVOICE_POSITION_COMPARISON_AUTO ?? false;
                                userOptionList.platform$invoice.ENABLE_CREATE_ACT_FROM_INVOICE =
                                    option.ENABLE_CREATE_ACT_FROM_INVOICE ?? false;
                                userOptionList.platform$invoice.SHOW_APPROVER_IN_STATUS_COLUMN =
                                    option.SHOW_APPROVER_IN_STATUS_COLUMN ?? false;
                            }
                            if (opt.table_name === 'act') {
                                userOptionList.platform$act.CONSIDER_TEMPLATE = option.CONSIDER_TEMPLATE;
                                userOptionList.platform$act.ENABLE_ACT_POSITION_COMPARISON_AUTO =
                                    option.ENABLE_ACT_POSITION_COMPARISON_AUTO ?? true;
                            }

                            if (opt.table_name === 'account') {
                                userOptionList.platform$account.IS_OUR_COMPANY_NAME_TEMPLATE =
                                    option?.IS_OUR_COMPANY_NAME_TEMPLATE;
                                userOptionList.platform$account.HAVING_A_CONTROLLED_ACCOUNT_TEMPLATE =
                                    option?.HAVING_A_CONTROLLED_ACCOUNT_TEMPLATE;
                            }

                            if (opt.table_name === 'contragent') {
                                // userOptionList.MULTIPLE_ORGANIZATION.USE = option.USE_MULTIPLE_ORGANIZATION;
                                // userOptionList.MULTIPLE_ORGANIZATION.SHOW_COLUMN_ORG =
                                //     option.MULTIPLE_ORGANIZATION_SHOW_COLUMN_ORG;
                                // userOptionList.MULTIPLE_ORGANIZATION.SHOW_FILTER_ORG =
                                //     option.MULTIPLE_ORGANIZATION_SHOW_FILTER_ORG;
                                userOptionList.platform$contragent.COUNTRY = option?.COUNTRY;
                                userOptionList.platform$contragent.DEFAULT_NDS = option?.DEFAULT_NDS;
                            }

                            if (opt.table_name === 'contract') {
                                userOptionList.platform$contract.ENABLE_WORK_VOLUME = option?.ENABLE_WORK_VOLUME;
                            }

                            if (opt.table_name === 'email') {
                                userOptionList.platform$email.ENABLE_EMAIL_REGISTRY = option?.ENABLE_EMAIL_REGISTRY;
                            }

                            if (opt.table_name === 'recognized_document') {
                                userOptionList.platform$recognized_document.available_service['platform.invoice'] =
                                    option.available_service?.['platform.invoice'] ?? 'Entera';
                                userOptionList.platform$recognized_document.available_service['pusk.commercial_offer'] =
                                    option.available_service?.['pusk.commercial_offer'] ?? 'Entera';
                                userOptionList.platform$recognized_document.available_service['platform.act'] =
                                    option.available_service?.['platform.act'] ?? 'Entera';
                                userOptionList.platform$recognized_document.ENABLE_DOC_COMPLIANCE_FOR_ACT =
                                    option.ENABLE_DOC_COMPLIANCE_FOR_ACT ?? false;
                                userOptionList.platform$recognized_document.ENABLE_DOC_COMPLIANCE_FOR_INVOICE =
                                    option.ENABLE_DOC_COMPLIANCE_FOR_INVOICE ?? false;
                                userOptionList.platform$recognized_document.IGNORE_INCORRECT_INN =
                                    option.IGNORE_INCORRECT_INN ?? false;
                                userOptionList.platform$recognized_document.GPT_MODEL = option.GPT_MODEL ?? null;
                                userOptionList.platform$recognized_document.GPT_REASONING_EFFORT =
                                    option.GPT_REASONING_EFFORT ?? 'none';
                                userOptionList.platform$recognized_document.GPT_TEMPERATURE =
                                    option.GPT_TEMPERATURE ?? 1;
                            }
                        }
                    }
                }
            });
        }

        document.getElementsByTagName('body')[0].classList.add('text-ag-theme-alpine');

        UserStore.setSysOptions(userOptionList);
        /** PUSK-5327 Заявки. Изменение наименования статуса "Согласование 1 этап" на "Согласование" */
        const REQUEST_APPROVAL_PROCESS_ENABLED =
            UserStore?.userState?.sysOptions?.pusk$request?.APPROVAL_PROCESS_ENABLED;
        NAME.REQUEST_DOC_STATE.started.label =
            NAME.REQUEST_DOC_STATE.started.action =
            NAME.REQUEST_DOC_STATE.started.name =
                REQUEST_APPROVAL_PROCESS_ENABLED ? 'Согласование' : 'Согласование 1 этап';
    },
};
